import { fetch } from 'whatwg-fetch';
import AbstractServerConnector from './AbstractServerConnector';

import authHelper from 'helpers/authHelper';
import getApiEndpoint from 'helpers/apiEndpoint';
import { objectToQueryString } from 'helpers/url';
import { getCurrentLanguage } from 'helpers/translate';

import {
  e400s,
  e500s,
  eAfterRequest,
  eBeforeRequest,
  eFailedToFetch,
  eFailedToParse,
  eNotFound,
} from 'helpers/apiEventHandler';

const apiEndpoint = getApiEndpoint();

export interface ServerResponse<T = unknown> {
  success: boolean;
  errors: string[];
  response: T;
  headers?: Record<string, string>;
}

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';
type RequestHeaders = Record<string, string>;

export default class AdminGatewayServerConnector extends AbstractServerConnector {
  resource: string;

  constructor(resource: string) {
    super();
    this.resource = resource;
  }

  get = <T = unknown>(
    route: string,
    data?: unknown,
    headers?: RequestHeaders
  ) => this.request<T>(route, 'GET', data, headers);

  put = <T = unknown>(
    route: string,
    data?: unknown,
    headers?: RequestHeaders
  ) => this.request<T>(route, 'PUT', data, headers);

  post = <T = unknown>(
    route: string,
    data?: unknown,
    headers?: RequestHeaders,
    json?: boolean
  ) => this.request<T>(route, 'POST', data, headers, json);

  delete = <T = unknown>(
    route: string,
    data?: unknown,
    headers?: RequestHeaders
  ) => this.request<T>(route, 'DELETE', data, headers);

  static handleResponseStatus(response: Response): Response {
    if (response.status === 200) {
      return response;
    }

    if (response.status === 401 || response.status === 403) {
      dispatchEvent(e400s);
      return response;
    }

    if (
      response.status === 400 ||
      response.status === 404 ||
      response.status === 405
    ) {
      dispatchEvent(eFailedToFetch);
    }

    if (response.status >= 500 && response.status <= 599) {
      dispatchEvent(e500s);
    }

    throw response.status;
  }

  static async handleResponseRawBody(
    response: Response
  ): Promise<ServerResponse> {
    try {
      return {
        ...(await response.json()),
        headers: Object.fromEntries(response.headers),
      };
    } catch (e) {
      console.error('FAILED_JSON_PARSE');
    }
    return {
      success: false,
      errors: ['FAILED_JSON_PARSE'],
      response: null,
    };
  }

  static handleResponseBody(response: ServerResponse): ServerResponse {
    if (response?.errors?.includes('NOT_FOUND')) {
      dispatchEvent(eNotFound);
    }
    return response;
  }

  getPrefix = (): string => '/api';

  getToken = (): string | null => authHelper.getToken();

  request<T = unknown>(
    url = '',
    method: HttpMethod,
    data: unknown = {},
    headers: RequestHeaders = {},
    json = true
  ): Promise<ServerResponse<T>> {
    const requestOptions: RequestInit = { method };

    let queryParams = '';

    if (method !== 'GET') {
      requestOptions.body = json ? JSON.stringify(data) : (data as BodyInit);
    } else {
      const paramString = objectToQueryString(data);
      if (paramString) {
        queryParams += '?' + paramString;
      }
    }

    const requestHeaders: RequestHeaders = {};
    const token = this.getToken();
    if (token) {
      requestHeaders['X-AUTH-TOKEN'] = 'Bearer ' + token;
    }

    const language = getCurrentLanguage();
    if (language) {
      requestHeaders['Language'] = language.toLowerCase();
    }
    if (json) {
      requestHeaders['Content-Type'] = 'application/json';
    }

    requestHeaders['Cache-Control'] = 'no-cache';
    requestHeaders['Pragma'] = 'no-cache';

    for (const headerKey in headers) {
      requestHeaders[headerKey] = headers[headerKey];
    }

    requestOptions.headers = requestHeaders;
    requestOptions.credentials = 'include';

    const endpoint =
      apiEndpoint + this.getPrefix() + this.resource + url + queryParams;
    dispatchEvent(eBeforeRequest);

    return fetch(endpoint, requestOptions)
      .then(AdminGatewayServerConnector.handleResponseStatus)
      .then(AdminGatewayServerConnector.handleResponseRawBody)
      .then(AdminGatewayServerConnector.handleResponseBody)
      .catch((error) => {
        if (error === 'FAILED_JSON_PARSE') {
          dispatchEvent(eFailedToParse);
        } else if (isNaN(error as number)) {
          dispatchEvent(eFailedToFetch);
        }
        throw error;
      })
      .finally(() => {
        dispatchEvent(eAfterRequest);
      }) as Promise<ServerResponse<T>>;
  }

  uploadLargeFile<T = unknown>(
    url: string,
    formData: FormData,
    headers: RequestHeaders = {},
    onProgress?: (event: ProgressEvent) => void,
    onStart?: (xhr: XMLHttpRequest) => void
  ): Promise<ServerResponse<T>> {
    return new Promise<ServerResponse<T>>((res, rej) => {
      let endpoint = apiEndpoint + this.getPrefix();

      if (typeof LARGE_FILE_ENDPOINT !== 'undefined') {
        endpoint = LARGE_FILE_ENDPOINT;
      }
      endpoint = endpoint + this.resource + url;

      const xhr = new XMLHttpRequest();
      xhr.open('post', endpoint);

      /*Set Headers*/
      const token = this.getToken();
      if (token) {
        xhr.setRequestHeader('X-AUTH-TOKEN', 'Bearer ' + token);
      }
      const language = getCurrentLanguage();
      if (language) {
        xhr.setRequestHeader('Language', language.toLowerCase());
      }
      xhr.setRequestHeader('Cache-Control', 'no-cache');
      xhr.setRequestHeader('Pragma', 'no-cache');
      for (const k in headers) {
        xhr.setRequestHeader(k, headers[k]);
      }
      /*end Set Headers*/

      dispatchEvent(eBeforeRequest);

      xhr.onload = () => {
        try {
          AdminGatewayServerConnector.handleResponseStatus({
            status: xhr.status,
          } as Response);

          const responseBody = JSON.parse(xhr.responseText);
          AdminGatewayServerConnector.handleResponseBody(responseBody);
          res(responseBody);
        } catch (e) {
          dispatchEvent(eFailedToParse);
          rej(e);
        }
      };

      xhr.onerror = (e) => {
        if (isNaN(e as unknown as number)) {
          dispatchEvent(eFailedToFetch);
        }
        rej(e);
      };

      if (xhr.upload && onProgress) {
        xhr.upload.onprogress = onProgress; // event.loaded / event.total * 100 ; //event.lengthComputable
      }

      xhr.send(formData);
      onStart && onStart(xhr);
    });
  }
}
